/**
 * TemplateVariableHighlighter
 *
 * Lightweight YAML highlighter for prompt documents. Tokenises:
 * - `# comments` → muted
 * - `key:` at the start of a line → blue
 * - `|` / `>` block scalar indicators → blue
 * - `{variable}` placeholders → amber (the substitution points)
 */

import React from 'react';

interface TemplateVariableHighlighterProps {
  content: string;
  className?: string;
}

type Token = { type: 'key' | 'comment' | 'var' | 'text'; value: string };

function tokenizeLine(line: string): Token[] {
  // In YAML, `#` is only a comment when preceded by whitespace or at
  // column 0 — NOT inside a value like `color: #FF0000`.
  const commentMatch = line.match(/(^#|(?<=\s)#)/);
  const commentIdx = commentMatch?.index ?? -1;
  const code = commentIdx >= 0 ? line.slice(0, commentIdx) : line;
  const comment = commentIdx >= 0 ? line.slice(commentIdx) : '';

  const tokens: Token[] = [];
  const keyMatch = code.match(/^(\s*)([A-Za-z_][\w-]*)(\s*:)(.*)$/);

  let remainder: string;
  if (keyMatch) {
    const [, indent, name, colon, rest] = keyMatch;
    if (indent) tokens.push({ type: 'text', value: indent });
    tokens.push({ type: 'key', value: name + colon });
    remainder = rest;
  } else {
    remainder = code;
  }

  // `matchAll` with a local regex — no shared-state `lastIndex` to reset,
  // no module-level global regex, no footgun if this function is ever
  // called concurrently or re-entered.
  let lastIdx = 0;
  for (const match of Array.from(remainder.matchAll(/\{([a-zA-Z_]\w*)}/g))) {
    const start = match.index ?? 0;
    if (start > lastIdx) {
      tokens.push({ type: 'text', value: remainder.slice(lastIdx, start) });
    }
    tokens.push({ type: 'var', value: match[0] });
    lastIdx = start + match[0].length;
  }
  if (lastIdx < remainder.length) {
    tokens.push({ type: 'text', value: remainder.slice(lastIdx) });
  }

  if (comment) tokens.push({ type: 'comment', value: comment });
  return tokens;
}

const TemplateVariableHighlighter: React.FC<TemplateVariableHighlighterProps> = ({
  content,
  className = '',
}) => {
  const lines = content.split('\n');

  return (
    <pre className={`whitespace-pre-wrap font-mono text-sm leading-relaxed ${className}`}>
      {lines.map((line, lineIdx) => {
        const tokens = tokenizeLine(line);
        return (
          <React.Fragment key={lineIdx}>
            {tokens.map((tok, i) => {
              if (tok.type === 'key') {
                return (
                  <span key={i} className="text-blue-700 font-semibold">
                    {tok.value}
                  </span>
                );
              }
              if (tok.type === 'comment') {
                return (
                  <span key={i} className="text-gray-400 italic">
                    {tok.value}
                  </span>
                );
              }
              if (tok.type === 'var') {
                return (
                  <span key={i} className="bg-amber-100 text-amber-800 rounded px-0.5">
                    {tok.value}
                  </span>
                );
              }
              return <span key={i}>{tok.value}</span>;
            })}
            {lineIdx < lines.length - 1 ? '\n' : ''}
          </React.Fragment>
        );
      })}
    </pre>
  );
};

export default TemplateVariableHighlighter;
